import React, { FC, useEffect } from 'react'
import { breakpoint, styled } from '@moonpig/launchpad-utils'
import { system as s } from '@moonpig/launchpad-system'
import { Box, Text } from '@moonpig/launchpad-components'
import { DepartmentsEnum } from '@moonpig/web-core-types-graphql'
import { useCoreLocaleText } from '@moonpig/web-core-locale-text'
import { useStoreId } from '@moonpig/web-core-stores'
import { Suggestions } from './Suggestions/Suggestions'
import { SuggestionType } from './Suggestions/Suggestion'
import { NavigationalSuggestionType } from './NavigationalSuggestions/NavigationalSuggestion'
import { NavigationalSuggestions } from './NavigationalSuggestions/NavigationalSuggestions'
import {
  DepartmentTabItemType,
  SearchFallbackItemType,
  SearchItemType,
  SubmitItemType,
  SuggestionItemType,
  UrlSuggestionItemType,
} from './types'
import { useLocaleText } from '../locale'
import {
  MAX_NAVIGATIONAL_SUGGESTIONS,
  MAX_SUGGESTIONS,
  SEARCH_BAR_HEIGHT_PX,
  MAX_CARD_SUGGESTIONS,
  MAX_FLOWERS_AND_PLANTS_SUGGESTIONS,
  MAX_GIFT_SUGGESTIONS,
} from './constants'
import {
  departmentMap,
  DepartmentType,
  filterStoreAllowedDepartments,
  getDepartmentByEnum,
} from './departmentMap'
import { FallbackSearchOption } from './Suggestions/FallbackSearchOption'

type AutocompleteSearchBoxProps = {
  isOpen: boolean
  suggestions: SuggestionType[]
  navigationalSuggestions: NavigationalSuggestionType[]
  inputValue: string | null
  getItemProps: (props: {
    itemIndex: number
    item:
      | SuggestionItemType
      | UrlSuggestionItemType
      | SearchItemType
      | DepartmentTabItemType
      | SearchFallbackItemType
  }) => Record<string, unknown>
  getMenuProps: () => Record<string, unknown>
  highlightedIndex: number | null
  value: string
  onSuggestionsVisible: () => void
  maxSuggestions?: number
  maxNavigationalSuggestions?: number
  navigationalHeaderText?: string
  suggestionsLoading?: boolean
  selectedDepartment: DepartmentType
  setActiveDepartment: (department: DepartmentType) => void
  setHighlightedIndex: (index: number) => void
  onSelectItem: (selectedValue: string | SubmitItemType) => void
} & React.HTMLProps<HTMLElement>

const StyledContainer = styled.div.attrs<{ isOpen: boolean }>(({ isOpen }) => ({
  className: !isOpen ? 'hidden' : undefined,
}))<{ isOpen: boolean } & React.HTMLAttributes<HTMLDivElement>>`
  position: relative;
  top: ${SEARCH_BAR_HEIGHT_PX};
  z-index: 3;
  overflow-y: auto;
  max-height: calc(100dvh - 48px);
  ${s({
    bgcolor: 'colorBackground01',
  })};

  &.hidden {
    display: none;
  }

  ${breakpoint('md')} {
    max-height: 75vh;
    overflow-y: auto;
    border-radius: 0px 0px 12px 12px;

    &::-webkit-scrollbar {
      width: 12px;
    }

    &::-webkit-scrollbar-track {
      ${s({
        bgcolor: 'colorBlack00',
      })}
      border-radius: 8px;
    }

    &::-webkit-scrollbar-thumb {
      ${s({
        bgcolor: 'colorBlack20',
      })}
      border-radius: 85px;
    }
  }
`

const StyledDepartmentHeader = styled(Text)`
  ${s({
    fontSize: '12px',
    lineHeight: '24px',
    fontWeight: 'bold',
    color: 'colorTextLabel',
    pl: 6,
    mb: 0,
  })};
`

const StyledSection = styled(Box)<{ shouldRenderDepartmentHeading: boolean }>(
  ({ shouldRenderDepartmentHeading }) =>
    s({
      mt: shouldRenderDepartmentHeading ? 6 : 0,
      borderBottom: 1,
      borderColor: 'colorBorder03',
    }),
)
export const AutocompleteSearchBox: FC<
  React.PropsWithChildren<AutocompleteSearchBoxProps>
> = ({
  isOpen,
  suggestions,
  navigationalSuggestions,
  inputValue,
  getItemProps,
  getMenuProps,
  highlightedIndex,
  value,
  onSuggestionsVisible,
  maxNavigationalSuggestions = MAX_NAVIGATIONAL_SUGGESTIONS,
  navigationalHeaderText = '',
  suggestionsLoading,
  selectedDepartment,
  setHighlightedIndex,
  onSelectItem,
}) => {
  const localiseNavText = useLocaleText()
  const localiseCoreText = useCoreLocaleText()

  const suggestionsVisible = isOpen && !!inputValue

  const showNavigationalSuggestions =
    navigationalSuggestions.length && suggestions.length < MAX_SUGGESTIONS - 1

  useEffect(() => {
    if (suggestionsVisible) {
      onSuggestionsVisible()
    }
  }, [onSuggestionsVisible, suggestionsVisible])

  const MAX_SUGGESTIONS_PER_TAB = MAX_SUGGESTIONS

  const storeId = useStoreId()

  const departmentTabsCount = filterStoreAllowedDepartments(storeId).length

  const cardSuggestions = suggestions.filter(
    suggestion => suggestion.department.enum === DepartmentsEnum.ALL_CARDS,
  )

  const giftSuggestions = suggestions.filter(
    suggestion => suggestion.department.enum === DepartmentsEnum.ALL_GIFTS,
  )

  const flowersAndPlantsSuggestions = suggestions.filter(
    suggestion =>
      suggestion.department.enum === DepartmentsEnum.ALL_FLOWERS_AND_PLANTS,
  )

  const shouldShowSuggestions = (targetDepartment: string) => {
    return ['all', targetDepartment].includes(selectedDepartment.name)
  }

  const shouldShowCardsSuggestions =
    shouldShowSuggestions(departmentMap.Cards.name) &&
    cardSuggestions.length > 0
  const shouldShowGiftsSuggestions =
    shouldShowSuggestions(departmentMap.Gifts.name) &&
    giftSuggestions.length > 0
  const shouldShowFlowersAndPlantsSuggestions =
    shouldShowSuggestions(departmentMap.FlowersAndPlants.name) &&
    flowersAndPlantsSuggestions.length > 0

  const maxSuggestionsPerDepartment = (tabDepartment: string) => {
    const departmentSuggestionsMap: Record<string, number> = {
      [departmentMap.Cards.name]: MAX_CARD_SUGGESTIONS,
      [departmentMap.Gifts.name]: MAX_GIFT_SUGGESTIONS,
      [departmentMap.FlowersAndPlants.name]: MAX_FLOWERS_AND_PLANTS_SUGGESTIONS,
    }

    return selectedDepartment.name === 'all'
      ? departmentSuggestionsMap[tabDepartment]
      : MAX_SUGGESTIONS_PER_TAB
  }
  const shouldRenderDepartmentHeading = selectedDepartment.name === 'all'

  const calculateStartIndex = (
    suggestionsLength: number,
    maxSuggestions: number,
  ): number => Math.min(maxSuggestions, suggestionsLength)

  const cardsStartIndex = departmentTabsCount

  const giftsStartIndex =
    selectedDepartment.name === 'all'
      ? cardsStartIndex +
        calculateStartIndex(cardSuggestions.length, MAX_CARD_SUGGESTIONS)
      : departmentTabsCount

  const flowerAndPlantsStartIndex =
    selectedDepartment.name === 'all'
      ? giftsStartIndex +
        calculateStartIndex(giftSuggestions.length, MAX_GIFT_SUGGESTIONS)
      : departmentTabsCount

  const calculateEmptySuggestionIndex = () => {
    const { name } = selectedDepartment

    const departmentLengths = {
      [departmentMap.All.name]:
        giftSuggestions.slice(0, MAX_GIFT_SUGGESTIONS).length +
        cardSuggestions.slice(0, MAX_CARD_SUGGESTIONS).length +
        flowersAndPlantsSuggestions.slice(0, MAX_FLOWERS_AND_PLANTS_SUGGESTIONS)
          .length +
        departmentTabsCount,
      [departmentMap.Cards.name]:
        cardSuggestions.slice(0, MAX_SUGGESTIONS_PER_TAB).length +
        departmentTabsCount,
      [departmentMap.Gifts.name]:
        giftSuggestions.slice(0, MAX_SUGGESTIONS_PER_TAB).length +
        departmentTabsCount,
      [departmentMap.FlowersAndPlants.name]:
        flowersAndPlantsSuggestions.slice(0, MAX_SUGGESTIONS_PER_TAB).length +
        departmentTabsCount,
    }

    return departmentLengths[name]
  }

  return (
    <StyledContainer
      isOpen={suggestionsVisible}
      onKeyDown={(e: React.KeyboardEvent<HTMLDivElement>) => {
        const { target, key, nativeEvent } = e
        const targetElement = target as HTMLDivElement

        if (key === 'Enter' && targetElement.textContent) {
          nativeEvent.preventDefault()
          const { searchterm, itemtype, department, searchkey } =
            targetElement.dataset

          if (itemtype === 'suggestion') {
            const submitItem = {
              department: getDepartmentByEnum(department as DepartmentsEnum),
              itemType: itemtype,
              searchKey: searchkey,
              searchterm,
              value: targetElement.textContent,
              type: 'suggestion',
            } as SuggestionItemType

            onSelectItem(submitItem)
          }

          if (itemtype === 'search-fallback') {
            const submitItem = {
              department: departmentMap[department as DepartmentsEnum],
              itemType: itemtype,
              value: searchterm,
            } as SearchFallbackItemType

            onSelectItem(submitItem)
          }
        }
      }}
    >
      {isOpen && suggestionsVisible ? (
        <div
          data-testid="lp-nav-autocomplete-searchbox"
          onMouseLeave={() => {
            setHighlightedIndex(-1)
          }}
        >
          {shouldShowCardsSuggestions && (
            <StyledSection
              shouldRenderDepartmentHeading={!!shouldRenderDepartmentHeading}
            >
              {shouldRenderDepartmentHeading && (
                <StyledDepartmentHeader as="h4">
                  {localiseCoreText('common.cards').toUpperCase()}
                </StyledDepartmentHeader>
              )}

              <Suggestions
                department={departmentMap.Cards}
                getItemProps={getItemProps}
                getMenuProps={getMenuProps}
                highlightedIndex={highlightedIndex}
                maxSuggestions={maxSuggestionsPerDepartment(
                  departmentMap.Cards.name,
                )}
                startIndex={cardsStartIndex}
                suggestions={cardSuggestions}
                suggestionsLoading={suggestionsLoading}
                value={value}
              />
            </StyledSection>
          )}

          {shouldShowGiftsSuggestions && (
            <StyledSection
              shouldRenderDepartmentHeading={!!shouldRenderDepartmentHeading}
            >
              {shouldRenderDepartmentHeading && (
                <StyledDepartmentHeader as="h4">
                  {localiseCoreText('common.gifts').toUpperCase()}
                </StyledDepartmentHeader>
              )}

              <Suggestions
                department={departmentMap.Gifts}
                getItemProps={getItemProps}
                getMenuProps={getMenuProps}
                highlightedIndex={highlightedIndex}
                maxSuggestions={maxSuggestionsPerDepartment(
                  departmentMap.Gifts.name,
                )}
                startIndex={giftsStartIndex}
                suggestions={giftSuggestions}
                suggestionsLoading={suggestionsLoading}
                value={value}
              />
            </StyledSection>
          )}

          {shouldShowFlowersAndPlantsSuggestions && (
            <StyledSection
              shouldRenderDepartmentHeading={!!shouldRenderDepartmentHeading}
            >
              {shouldRenderDepartmentHeading && (
                <StyledDepartmentHeader as="h4">
                  {localiseCoreText('common.flowers_and_plants').toUpperCase()}
                </StyledDepartmentHeader>
              )}
              <Suggestions
                department={departmentMap.FlowersAndPlants}
                getItemProps={getItemProps}
                getMenuProps={getMenuProps}
                highlightedIndex={highlightedIndex}
                maxSuggestions={maxSuggestionsPerDepartment(
                  departmentMap.FlowersAndPlants.name,
                )}
                startIndex={flowerAndPlantsStartIndex}
                suggestions={flowersAndPlantsSuggestions}
                suggestionsLoading={suggestionsLoading}
                value={value}
              />
            </StyledSection>
          )}
          {showNavigationalSuggestions ? (
            <StyledSection
              shouldRenderDepartmentHeading={!!shouldRenderDepartmentHeading}
            >
              <StyledDepartmentHeader as="h4">
                {navigationalHeaderText === ''
                  ? localiseNavText(
                      'search.navigational_suggestions_header',
                    ).toUpperCase()
                  : navigationalHeaderText.toUpperCase()}
              </StyledDepartmentHeader>
              <NavigationalSuggestions
                getItemProps={getItemProps}
                getMenuProps={getMenuProps}
                maxNavigationalSuggestions={maxNavigationalSuggestions}
                navigationalSuggestions={navigationalSuggestions}
              />
            </StyledSection>
          ) : null}
          <FallbackSearchOption
            department={selectedDepartment}
            getItemProps={getItemProps}
            highlighted={highlightedIndex === calculateEmptySuggestionIndex()}
            searchTerm={value}
          />
        </div>
      ) : null}
    </StyledContainer>
  )
}
